// pages/Roadmap.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Stepper,
  Step,
  StepLabel,
  Chip,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip  // ✅ Renamed to avoid conflict
} from '@mui/material';
import {
  Timeline,
  Psychology,
  AutoGraph,
  TrendingUp,
  AttachMoney,
  Group,
  Schedule,
  CheckCircle,
  Warning,
  Edit,
  Delete,
  Add,
  ArrowForward,
  Download,
  Share,
  CompareArrows,
  Refresh,
  RocketLaunch,
  ShowChart
} from '@mui/icons-material';
import { useIEP } from '../contexts/IEPContext';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip,  // ✅ Renamed to avoid conflict
  Legend, 
  ResponsiveContainer 
} from 'recharts';

// Need to import Alert component
import { Alert } from '@mui/material';

const Roadmap = ({ organization }) => {
  const { roadmap, iepEngine, updateOrganization, generateCustomRoadmap } = useIEP();
  const [activeStep, setActiveStep] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [showSimulation, setShowSimulation] = useState(false);
  const [showOptimization, setShowOptimization] = useState(false);
  const [customConstraints, setCustomConstraints] = useState({
    maxBudget: 1000000,
    maxTimeline: 365,
    teamCapacity: 10,
    riskTolerance: 0.5
  });
  const [simulationResults, setSimulationResults] = useState(null);
  const [optimizedRoadmap, setOptimizedRoadmap] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (roadmap) {
      // Initialize with first phase
      setActiveStep(0);
    }
  }, [roadmap]);

  const handleStepClick = (step) => {
    setActiveStep(step);
  };

  const runSimulation = () => {
    if (!roadmap || !iepEngine) return;
    
    // Run simulation
    const results = iepEngine.simulateImpact(
      roadmap.phases.flatMap(p => p.transformations),
      customConstraints,
      organization
    );
    
    setSimulationResults(results);
    setShowSimulation(true);
  };

  const runOptimization = () => {
    if (!roadmap || !iepEngine) return;
    
    // Get all transformations from roadmap
    const allTransformations = roadmap.phases.flatMap(p => p.transformations);
    
    // Run optimization
    const optimized = iepEngine.qStarOptimizeSequence(
      allTransformations,
      customConstraints
    );
    
    // Create new roadmap from optimized sequence
    const newRoadmap = {
      ...roadmap,
      phases: [
        {
          name: "AI-Optimized Phase 1",
          duration: "Quarter 1",
          transformations: optimized.sequence.slice(0, 3),
          focus: "AI-optimized quick wins"
        },
        {
          name: "AI-Optimized Phase 2",
          duration: "Quarters 2-3",
          transformations: optimized.sequence.slice(3, 7),
          focus: "Core value delivery"
        },
        {
          name: "AI-Optimized Phase 3",
          duration: "Quarter 4+",
          transformations: optimized.sequence.slice(7),
          focus: "Optimization & scaling"
        }
      ],
      resourceAllocation: {
        ...roadmap.resourceAllocation,
        totalBudget: optimized.totalCost,
        totalDuration: optimized.totalDuration
      }
    };
    
    setOptimizedRoadmap(newRoadmap);
    setShowOptimization(true);
  };

  const applyOptimization = () => {
    if (optimizedRoadmap) {
      // Update organization with optimized roadmap
      updateOrganization({
        ...organization,
        roadmap: optimizedRoadmap
      });
      setShowOptimization(false);
    }
  };

  const exportRoadmap = () => {
    const data = {
      roadmap,
      simulationResults,
      organization
    };
    
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'iep-roadmap.json';
    a.click();
  };

  const generateTimelineData = () => {
    if (!roadmap?.phases) return [];
    
    const data = [];
    let cumulativeDays = 0;
    
    roadmap.phases.forEach((phase, index) => {
      const days = phase.duration.includes('Quarter') ? 90 : 180;
      cumulativeDays += days;
      
      data.push({
        phase: `Phase ${index + 1}`,
        days: cumulativeDays,
        value: phase.transformations.reduce((sum, t) => sum + (t.impactScore || 0.5), 0) * 100,
        cost: phase.transformations.reduce((sum, t) => sum + (t.cost || 100000), 0)
      });
    });
    
    return data;
  };

  if (!roadmap) {
    return (
      <Box textAlign="center" py={8}>
        <Timeline sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No roadmap available
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Complete organization setup to generate your transformation roadmap
        </Typography>
        <Button variant="contained" href="/">
          Setup Organization
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h1" gutterBottom>
            Transformation Roadmap
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Your AI-optimized journey to organizational excellence
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Psychology />}
            onClick={runOptimization}
          >
            AI Optimize
          </Button>
          <Button
            variant="outlined"
            startIcon={<ShowChart />}
            onClick={runSimulation}
          >
            Run Simulation
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={exportRoadmap}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* ROI Summary */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main">
                  {roadmap.resourceAllocation?.roiEstimate?.roiPercentage || '0'}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expected ROI
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">
                  ${(roadmap.resourceAllocation?.totalBudget / 1000000).toFixed(1)}M
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Investment
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">
                  {roadmap.phases?.reduce((sum, phase) => sum + (phase.transformations?.length || 0), 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Transformations
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">
                  {roadmap.phases?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Phases
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Timeline View" />
        <Tab label="Resource View" />
        <Tab label="Impact Analysis" />
        <Tab label="Progress Tracking" />
      </Tabs>

      {/* Timeline View */}
      {activeTab === 0 && (
        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent>
            <Stepper activeStep={activeStep} alternativeLabel>
              {roadmap.phases?.map((phase, index) => (
                <Step key={index}>
                  <StepLabel
                    onClick={() => handleStepClick(index)}
                    sx={{ cursor: 'pointer' }}
                    optional={
                      <Typography variant="caption" color="text.secondary">
                        {phase.transformations?.length || 0} transformations
                      </Typography>
                    }
                  >
                    {phase.name}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Active Phase Details */}
            {roadmap.phases?.[activeStep] && (
              <Box mt={4}>
                <Typography variant="h5" gutterBottom>
                  {roadmap.phases[activeStep].name}
                </Typography>
                <Typography variant="body1" paragraph>
                  {roadmap.phases[activeStep].focus}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Duration
                      </Typography>
                      <Typography variant="h6">
                        {roadmap.phases[activeStep].duration}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Expected Improvement
                      </Typography>
                      <Typography variant="h6">
                        {roadmap.phases[activeStep].expectedImprovement}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Transformations in this phase */}
                <Typography variant="h6" gutterBottom mt={4}>
                  Transformations in this Phase
                </Typography>
                <Grid container spacing={2}>
                  {roadmap.phases[activeStep].transformations?.map((transformation, idx) => (
                    <Grid item xs={12} md={6} key={idx}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {transformation.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {transformation.description}
                          </Typography>
                          <Box display="flex" justifyContent="space-between">
                            <Chip 
                              label={`Impact: ${(transformation.impactScore * 100).toFixed(0)}%`}
                              size="small"
                              color="primary"
                            />
                            <Chip 
                              label={`${transformation.timeline}`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Resource View */}
      {activeTab === 1 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resource Allocation
            </Typography>
            
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={roadmap.resourceAllocation?.quarterlyBreakdown || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="phase" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="budget" fill="#6366f1" name="Budget (USD)" />
                <Bar dataKey="teamSize" fill="#10b981" name="Team Size" />
              </BarChart>
            </ResponsiveContainer>

            <Grid container spacing={2} mt={3}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Budget Required
                  </Typography>
                  <Typography variant="h4">
                    ${(roadmap.resourceAllocation?.totalBudget || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Expected ROI
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {roadmap.resourceAllocation?.roiEstimate?.roiPercentage || '0'}%
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Impact Analysis */}
      {activeTab === 2 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cumulative Impact Over Time
            </Typography>
            
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={generateTimelineData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="phase" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip />
                <Legend />
                <Area 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="value" 
                  stroke="#6366f1" 
                  fill="#6366f1" 
                  fillOpacity={0.3}
                  name="Cumulative Value"
                />
                <Area 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="cost" 
                  stroke="#10b981" 
                  fill="#10b981" 
                  fillOpacity={0.3}
                  name="Cumulative Cost"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Progress Tracking */}
      {activeTab === 3 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Progress Tracking
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transformation</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roadmap.phases?.flatMap((phase, phaseIndex) => 
                    phase.transformations?.map((transformation, transIndex) => {
                      const progress = Math.floor(Math.random() * 100);
                      const status = progress === 0 ? 'Not Started' : 
                                    progress < 100 ? 'In Progress' : 'Completed';
                      
                      return (
                        <TableRow key={`${phaseIndex}-${transIndex}`}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {transformation.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`Phase ${phaseIndex + 1}`}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={status}
                              size="small"
                              color={
                                status === 'Completed' ? 'success' :
                                status === 'In Progress' ? 'warning' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <LinearProgress 
                                variant="determinate" 
                                value={progress}
                                sx={{ width: 80, height: 6, mr: 2 }}
                                color={
                                  progress < 30 ? 'error' :
                                  progress < 70 ? 'warning' : 'success'
                                }
                              />
                              <Typography variant="body2">
                                {progress}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(Date.now() - (phaseIndex * 30 * 24 * 60 * 60 * 1000)).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(Date.now() + ((phaseIndex + 1) * 30 * 24 * 60 * 60 * 1000)).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="small" 
                              variant="text"
                              onClick={() => {
                                // Navigate to transformation detail
                                window.location.href = `/iep/transformations#${transformation.id}`;
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => setEditMode(!editMode)}
        >
          {editMode ? 'Exit Edit Mode' : 'Edit Roadmap'}
        </Button>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Share />}
          >
            Share Roadmap
          </Button>
          <Button
            variant="contained"
            startIcon={<RocketLaunch />}
            href="/ai-optimizer"
          >
            AI Optimize Further
          </Button>
        </Box>
      </Box>

      {/* Simulation Dialog */}
      <Dialog 
        open={showSimulation} 
        onClose={() => setShowSimulation(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <ShowChart sx={{ verticalAlign: 'middle', mr: 1 }} />
          Roadmap Simulation Results
        </DialogTitle>
        <DialogContent dividers>
          {simulationResults ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Simulation ran with {simulationResults.scenarios} scenarios
              </Typography>
              
              <Grid container spacing={2} mt={2}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Success Probability
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {simulationResults.successProbability}%
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Confidence Interval
                    </Typography>
                    <Typography variant="h4">
                      {simulationResults.confidenceInterval}%
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" gutterBottom mt={3}>
                Risk Assessment:
              </Typography>
              <List>
                {simulationResults.risks?.map((risk, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Warning color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={risk.type}
                      secondary={`Probability: ${risk.probability}% | Impact: ${risk.impact}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Typography>Running simulation...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSimulation(false)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            onClick={runOptimization}
          >
            Optimize Based on Results
          </Button>
        </DialogActions>
      </Dialog>

      {/* Optimization Dialog */}
      <Dialog 
        open={showOptimization} 
        onClose={() => setShowOptimization(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Psychology sx={{ verticalAlign: 'middle', mr: 1 }} />
          AI-Optimized Roadmap
        </DialogTitle>
        <DialogContent dividers>
          {optimizedRoadmap ? (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>AI Optimization Complete:</strong> Q* framework has optimized your roadmap 
                  for maximum efficiency and ROI.
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      {((roadmap.resourceAllocation?.totalBudget - optimizedRoadmap.resourceAllocation?.totalBudget) / roadmap.resourceAllocation?.totalBudget * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Cost Reduction
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      {((optimizedRoadmap.resourceAllocation?.roiEstimate?.roiPercentage - roadmap.resourceAllocation?.roiEstimate?.roiPercentage) || 0).toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ROI Improvement
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      {((roadmap.resourceAllocation?.totalDuration - optimizedRoadmap.resourceAllocation?.totalDuration) / roadmap.resourceAllocation?.totalDuration * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text-secondary">
                      Time Saved
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      {optimizedRoadmap.resourceAllocation?.efficiency?.toFixed(2) || 1.0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Efficiency Score
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" gutterBottom mt={3}>
                Optimized Phase Sequence:
              </Typography>
              <Stepper orientation="vertical" sx={{ mt: 2 }}>
                {optimizedRoadmap.phases?.map((phase, index) => (
                  <Step key={index} active>
                    <StepLabel>
                      <Typography variant="subtitle1">{phase.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {phase.focus} | {phase.transformations?.length || 0} transformations
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          ) : (
            <Box textAlign="center" py={4}>
              <Psychology sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography>Running AI optimization...</Typography>
              <LinearProgress sx={{ width: '60%', mx: 'auto', mt: 2 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOptimization(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={applyOptimization}
            disabled={!optimizedRoadmap}
          >
            Apply Optimized Roadmap
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


export default Roadmap;